import { ButtonUI, InputField, MessageErrors, Notification } from '@/components';
import {
	INFO_ORDER_USER,
	MESSSAGE_GLOBAL,
	REGEX_CHARACTER_SPECIAL,
	REGEX_EMAIL,
	REGEX_PHONE_NUMBER,
} from '@/constant';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { ICart, ICartInfoItem, IOrderInfo, IParamCreateOrder } from '@/models';
import { cartActions, cartSelectors } from '@/store/reducers/cartSlice';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useState } from 'react';
import { Controller, useForm, UseFormReturn } from 'react-hook-form';
import * as yup from 'yup';
import CartItemOrder from './CartItemOrder';
import OrderPage from './OrderPage';
import { CartItemV2Props } from './CartItemV2';
import { request } from '@/utils';
import { CartItem, useCart } from '@/modules/shared/hooks';

interface FormOrder {
	data?: ICart;
	formatCartList?: CartItemV2Props[];
	onChangeActiveStep?: (val: boolean) => void;
	onChangeLocalCart?: (list: CartItem[]) => void;
}

interface IOrderSuccess {
	isActive: boolean;
	data: IOrderInfo | null;
	code: string;
}

const FormOrder: React.FC<FormOrder> = ({
	data,
	formatCartList,
	onChangeActiveStep,
	onChangeLocalCart,
}) => {
	const dispatch = useAppDispatch();
	const [loading, setLoading] = React.useState(false);
	const { clearCart } = useCart();
	const cartSelector = useAppSelector(cartSelectors);
	const [isOrderSuccess, setIsOrderSuccess] = useState<IOrderSuccess>({
		isActive: false,
		data: null,
		code: '',
	});
	const formsSchema = yup.object<Record<keyof IParamCreateOrder, yup.AnySchema>>().shape({
		customerName: yup
			.string()
			.trim()
			.required('Vui lòng nhập họ tên')
			.matches(REGEX_CHARACTER_SPECIAL, 'Vui lòng loại bỏ ký tự đặc biệt'),
		phone: yup
			.string()
			.required('Vui lòng nhập số điện thoại')
			.matches(REGEX_PHONE_NUMBER, 'Lỗi định dạng SĐT'),
		email: yup
			.string()
			.required('Vui lòng nhập email')
			.test('valid-email', 'Email không đúng định dạng', (value) => {
				if (!value) return true;
				if (!REGEX_EMAIL.test(value)) {
					return false;
				}
				return true;
			}),
		address: yup.string().required('Vui lòng nhập địa chỉ'),
		//note: yup.string().required('Vui lòng địa chỉ'),
	});

	const initalValues: IParamCreateOrder = {
		address: '',
		customerName: '',
		email: '',
		phone: '',
		note: '',
	};
	const methods: UseFormReturn<IParamCreateOrder> = useForm<IParamCreateOrder>({
		mode: 'onChange',
		shouldFocusError: true,
		defaultValues: initalValues,
		resolver: yupResolver(formsSchema),
	});

	useEffect(() => {
		const valuesHasInfo = localStorage.getItem(INFO_ORDER_USER);
		if (valuesHasInfo) {
			methods.reset(JSON.parse(valuesHasInfo));
		}
	}, []);

	const formatConfig: ICartInfoItem[] =
		formatCartList && formatCartList.length > 0
			? formatCartList.map((item) => {
					return {
						product_id: item.id,
						product_image: item.image ?? '',
						product_name: item.name,
						price: String(item.variants.salePrice),
						quantity: item.quantity,
						variation_name: item.variants.attributes.map((item) => item.value).join(' - '),
						variation_id: item.variants.id,
					};
			  })
			: [];

	const submitData = async (values: IParamCreateOrder) => {
		setLoading(true);
		try {
			const result = await createOrder({
				address: values.address,
				customerName: values.customerName,
				email: values.email,
				phone: values.phone,
				// note: values.note,
				totalPrice: cartSelector.dataCart.totalPrice,
				variants: formatConfig.map((item) => {
					return {
						id: item.variation_id,
						quantity: item.quantity,
					};
				}),
			});

			if (result.data) {
				clearCart();
				// onChangeLocalCart?.([]);
				setIsOrderSuccess({
					...isOrderSuccess,
					isActive: true,
					code: result.data.code,
				});
			}
			setLoading(false);
		} catch (error) {
			setLoading(false);
			setIsOrderSuccess({
				...isOrderSuccess,
				isActive: false,
				// code: result.data.code,
			});

			Notification.Info.default(MESSSAGE_GLOBAL, 'ERROR', 3000);
		}
	};

	const onSubmit = (values: IParamCreateOrder) => {
		submitData(values);
	};

	return !isOrderSuccess?.isActive ? (
		<div className='lg:container'>
			<form onSubmit={methods.handleSubmit(onSubmit)} className='relative'>
				<div className='p-2.5 lg:p-0 '>
					<div className='flex flex-col lg:flex-row relative gap-[40px] lg:gap-[70px] py-4 lg:py-16'>
						<div className='flex-auto'>
							<div className='flex flex-col gap-12'>
								<div className='flex flex-col gap-3 lg:gap-6'>
									<h3 className='text-large font-bold text-primary mb-4 lg:mb-11'>
										Thông tin giao hàng *
									</h3>
									<Controller
										render={({ field, formState }) => (
											<InputField
												typeStyleForms={{ animation: true, labelInsideBorder: true }}
												id='customerName'
												{...field}
												label='Họ và Tên'
												validationMessage={
													<MessageErrors name='customerName' errors={formState.errors} />
												}
											/>
										)}
										name='customerName'
										control={methods.control}
									/>
									<Controller
										render={({ field, formState }) => (
											<InputField
												typeStyleForms={{ animation: true, labelInsideBorder: true }}
												id='phone'
												{...field}
												type='tel'
												label='Số điện thoại'
												validationMessage={<MessageErrors name='phone' errors={formState.errors} />}
											/>
										)}
										name='phone'
										control={methods.control}
									/>
									<Controller
										render={({ field, formState }) => (
											<InputField
												typeStyleForms={{ animation: true, labelInsideBorder: true }}
												id='address'
												{...field}
												label='Địa chỉ'
												validationMessage={
													<MessageErrors name='address' errors={formState.errors} />
												}
											/>
										)}
										name='address'
										control={methods.control}
									/>
									<Controller
										render={({ field, formState }) => (
											<InputField
												typeStyleForms={{ animation: true, labelInsideBorder: true }}
												id='note'
												{...field}
												label='Ghi chú'
												validationMessage={<MessageErrors name='note' errors={formState.errors} />}
											/>
										)}
										name='note'
										control={methods.control}
									/>
								</div>
								<div className='flex flex-col gap-3 lg:gap-6'>
									<h3 className='text-large font-bold text-primary mb-4 lg:mb-11'>Email</h3>
									<Controller
										render={({ field, formState }) => (
											<InputField
												typeStyleForms={{ animation: true, labelInsideBorder: true }}
												id='email'
												type='email'
												{...field}
												label='Địa chỉ Email'
												validationMessage={<MessageErrors name='email' errors={formState.errors} />}
											/>
										)}
										name='email'
										control={methods.control}
									/>
								</div>
							</div>
							<div className='hidden lg:block'>
								<ButtonUI
									color='primary'
									htmlType='submit'
									text='Tiến hành giao hàng'
									loading={loading}
									disabled={loading}
									className='text-medium flex mt-12 justify-center w-full'
								/>
								<span
									className='text-[#71717A] flex cursor-pointer justify-center mt-6'
									onClick={() => onChangeActiveStep?.(false)}
								>
									Quay lại giỏ hàng
								</span>
							</div>
						</div>
						<div className='h-full sticky top-0 flex-auto lg:flex-[0_0_55%] lg:max-w-[55%]'>
							<div className='bg-base-100 h-fit p-2.5 lg:p-[54px]'>
								<h2 className='font-bold text-[32px] mb-[50px]'>Tóm tắt đơn hàng</h2>
								{formatConfig?.length ? (
									<>
										<div className='max-h-[425px] overflow-y-auto'>
											{formatConfig?.map((k, i) => (
												<CartItemOrder data={k} key={i} />
											))}
										</div>
										<div className='flex items-center text-primary justify-between pt-[20px] lg:pt-[40px]'>
											<span className='text-base lg:text-medium font-bold'>Tổng chi phí</span>
											<span className='text-large lg:text-large-2x font-bold'>
												{Number(cartSelector.dataCart.totalPrice)?.toLocaleString('it-IT')}₫
											</span>
										</div>
										<div className='flex text-primary item gap-[22px] mt-[20px] lg:mt-[40px]'>
											<InformationCircleIcon width={70} />
											<span className='text-small'>
												Giá tiền chưa bao gồm phí vận chuyển. Sau khi Nga Liêm nhận đơn hàng sẽ có
												nhân viên hỗ trợ, tư vấn cho mình về chi phí vận chuyển.
											</span>
										</div>
									</>
								) : null}
							</div>
						</div>
					</div>
				</div>
				<div className='flex shadow-top mb-4 flex-row-reverse gap-4 bg-base-100 items-center lg:hidden sticky bottom-0'>
					<ButtonUI
						color='primary'
						htmlType='submit'
						text='Tiến hành giao hàng'
						className='text-small rounded-none !my-0 flex justify-center w-full'
						loading={loading}
						disabled={loading}
					/>
					<ButtonUI
						color='outline'
						htmlType='button'
						text='Trở về'
						onClick={() => onChangeActiveStep?.(false)}
						className='text-small rounded-none !my-0 flex justify-center w-full'
					/>
				</div>
			</form>
		</div>
	) : (
		<OrderPage
			status={isOrderSuccess?.data!}
			dataCode={isOrderSuccess.code}
			formatConfig={formatConfig}
		/>
	);
};

export default FormOrder;

export const createOrder = (data: {
	totalPrice: number;
	customerName: string;
	phone: string;
	address: string;
	email: string;
	note?: string;
	variants: {
		id: number;
		quantity: number;
	}[];
}): Promise<any> => {
	const params = {
		totalPrice: 12000,
		customerName: 'Quang',
		phone: '0908986784',
		address: 'adsffd asfd asfad s',
		email: 'test@gmail.com',
		variants: [
			{
				id: 1,
				quantity: 12,
			},
		],
	};

	return request({
		baseURL: process.env.NEXT_PUBLIC_DOMAIN_API_AUTH!,
		url: '/order/create',
		method: 'POST',
		data,
	});
};
